import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';

import {
  insightCardLine,
  insightDiscussCta,
  insightStoryBeats,
} from '../../config/insightAccess';
import { useAssistant } from '../../contexts/AssistantContext';
import { useAuth } from '../../contexts/AuthContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import type { InsightCard } from '../../services/assistantService';

type Props = {
  card: InsightCard;
  widget: string;
  /** First featured card starts open so the story is visible. */
  defaultExpanded?: boolean;
  style?: StyleProp<ViewStyle>;
};

/**
 * One-line title on top. Observation → Interpretation → Impact → Action
 * inside when expanded. Explore still opens chat.
 */
export function InsightExpandableCard({
  card,
  widget,
  defaultExpanded = false,
  style,
}: Props) {
  const { openAssistant } = useAssistant();
  const { user } = useAuth();
  const isPro = user?.plan === 'pro';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const line = insightCardLine(card);
  const beats = insightStoryBeats(card);
  const cta = insightDiscussCta(isPro);

  const openChat = () => {
    void openAssistant({
      insightId: card.id,
      insight: card,
      insightTitle: line || card.title,
      starterPrompt: card.ctaPrompt,
      widget,
    });
  };

  return (
    <View style={[styles.shell, style]}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`${line}. ${expanded ? 'Hide detail' : 'Show detail'}`}
        onPress={() => setExpanded((prev) => !prev)}
        style={({ pressed }) => [styles.header, pressed && styles.pressed]}
      >
        <Text style={styles.title} numberOfLines={expanded ? 3 : 1}>
          {line}
        </Text>
        <Text style={[styles.chevron, expanded && styles.chevronOpen]}>›</Text>
      </Pressable>

      {expanded ? (
        <View style={styles.body}>
          {beats.length > 0 ? (
            <View style={styles.beats}>
              {beats.map((beat) => (
                <View key={beat.label} style={styles.beat}>
                  <Text style={styles.beatLabel}>{beat.label}</Text>
                  <Text style={styles.beatText}>{beat.text}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.beatText}>
              Open Explore to walk through this with the assistant.
            </Text>
          )}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={isPro ? `Explore: ${line}` : `Upgrade to explore: ${line}`}
            onPress={openChat}
            style={({ pressed }) => [styles.ctaBtn, pressed && styles.pressed]}
          >
            <Text style={styles.cta}>{cta}</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    borderRadius: radii.lg,
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.22)',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    cursor: 'pointer',
  } as ViewStyle,
  title: {
    ...typography.body,
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
    lineHeight: 21,
  },
  chevron: {
    ...typography.headline,
    fontSize: 22,
    lineHeight: 24,
    color: colors.textMuted,
    transform: [{ rotate: '90deg' }],
  },
  chevronOpen: {
    transform: [{ rotate: '270deg' }],
    color: colors.accent,
  },
  body: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(61, 214, 164, 0.16)',
    paddingTop: spacing.sm,
  },
  beats: {
    gap: spacing.sm,
  },
  beat: {
    gap: 2,
  },
  beatLabel: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.accent,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  beatText: {
    ...typography.body,
    fontSize: 14,
    color: colors.text,
    lineHeight: 20,
  },
  ctaBtn: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
    paddingRight: spacing.sm,
    cursor: 'pointer',
  } as ViewStyle,
  cta: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
  pressed: {
    opacity: 0.88,
  },
});
